import { useCallback, useEffect, useRef, useState } from "react";
import { toApiError } from "../../../lib/http/errors";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { ErrorBanner, Loading, ToastHost, useToasts } from "../../../components/Shell";
import { useT } from "../../../i18n";
import { CATALOG_TYPES } from "../api/opsApi";
import type { OpsApi } from "../api/opsApi";
import type { MonitorPod, MonitorTiles, RoomDetail, RoomMonitor } from "../types";

/** 모니터 다시 읽기 — 서버가 5초 캐시(지표 ≤60초 지연 허용) */
export const MONITOR_POLL_MS = 5000;

const dash = "—";
const pct = (v: number | null, digits = 1) => (v == null ? dash : `${(v * 100).toFixed(digits)}%`);

/** 경고 문턱(와이어 '.kpi.warn') — 틱 p95 > 8ms · 열화 발동 · FE-303 ≥ 1% */
export function tileWarn(k: keyof MonitorTiles, tiles: MonitorTiles): boolean {
  if (k === "tickP95Ms") return (tiles.tickP95Ms ?? 0) > 8;
  if (k === "degrade") return Object.values(tiles.degrade).some((n) => n > 0);
  if (k === "fe303Rate") return (tiles.fe303Rate ?? 0) >= 0.01;
  return false;
}

export function tileValue(k: keyof MonitorTiles, tiles: MonitorTiles): string {
  switch (k) {
    case "tickP95Ms":
      return tiles.tickP95Ms == null ? dash : `${tiles.tickP95Ms.toFixed(1)}ms`;
    case "downKbps":
      return tiles.downKbps == null ? dash : `${Math.round(tiles.downKbps)}kbps`;
    case "reconnectRate":
      return pct(tiles.reconnectRate);
    case "fe303Rate":
      return pct(tiles.fe303Rate);
    case "degrade": {
      const on = (["s1", "s2", "s3", "s4"] as const).filter((s) => tiles.degrade[s] > 0);
      return on.length
        ? on.map((s) => `${s.toUpperCase()} ×${tiles.degrade[s]}`).join(" · ")
        : dash;
    }
    case "modeCompletion":
      return pct(tiles.modeCompletion, 0);
    case "activeRooms":
      return String(tiles.activeRooms);
    case "joinP95S":
      return tiles.joinP95S == null ? dash : `${tiles.joinP95S.toFixed(1)}s`;
  }
}

const TILES: readonly (keyof MonitorTiles)[] = [
  "tickP95Ms",
  "downKbps",
  "reconnectRate",
  "fe303Rate",
  "degrade",
  "modeCompletion",
  "activeRooms",
  "joinP95S",
];

/**
 * FS-15-01 룸 모니터(운영자 · PH2 · SCR v0.6 sfs-15) — 지표 8종 타일 · 룸 목록(코스 가명) · 알림(확인)
 * + Pod 목록과 드레인 토글(와이어 밖 — SR-OPS-03 운영 조작) · 룸 상세(타임라인 · 가명 pid 만) · 카탈로그 요약.
 * 개인 식별 없음 — 코스는 기관·context 가명, pid 는 가명만.
 */
export function MonitorTab({
  api,
  onSummary,
}: {
  api: OpsApi;
  onSummary: (rooms: number, alerts: number) => void;
}) {
  const t = useT();
  const [data, setData] = useState<RoomMonitor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acked, setAcked] = useState<ReadonlySet<string>>(new Set());
  const [detail, setDetail] = useState<RoomDetail | "loading" | null>(null);
  const [busyPod, setBusyPod] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<Map<string, number> | null>(null);
  const toasts = useToasts();
  const inflight = useRef(false);

  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      setData(await api.roomMonitor());
      setError(null);
    } catch (e) {
      setError(toApiError(e).code);
    } finally {
      inflight.current = false;
    }
  }, [api]);

  useEffect(() => {
    void load();
    const h = window.setInterval(() => void load(), MONITOR_POLL_MS);
    return () => window.clearInterval(h);
  }, [load]);

  useEffect(() => {
    let alive = true;
    api
      .catalog()
      .then((items) => {
        if (!alive) return;
        const m = new Map<string, number>();
        for (const it of items) if (it.enabled) m.set(it.type, (m.get(it.type) ?? 0) + 1);
        setCatalog(m);
      })
      .catch(() => alive && setCatalog(new Map()));
    return () => {
      alive = false;
    };
  }, [api]);

  const alertKey = (a: RoomMonitor["alerts"][number]) => `${a.roomId ?? ""}:${a.kind}`;
  const alerts = (data?.alerts ?? []).filter((a) => !acked.has(alertKey(a)));
  useEffect(() => {
    if (data) onSummary(data.tiles.activeRooms, alerts.length);
  }, [data, alerts.length, onSummary]);

  const drain = async (p: MonitorPod) => {
    setBusyPod(p.podId);
    try {
      await api.drainPod(p.podId, !p.draining);
      toasts.push(
        "info",
        t(p.draining ? "ops.monitor.undrained" : "ops.monitor.drained", { pod: p.podId }),
      );
      await load();
    } catch (e) {
      toasts.push("error", t("ops.common.saveFailed"), toApiError(e).code);
    } finally {
      setBusyPod(null);
    }
  };

  const openDetail = async (roomId: string) => {
    setDetail("loading");
    try {
      setDetail(await api.roomDetail(roomId));
    } catch (e) {
      setDetail(null);
      toasts.push("error", t("ops.common.loadFailed"), toApiError(e).code);
    }
  };

  const modeLabel = (id: string | null) =>
    id && ["lobby", "pushout-ring", "carry-together"].includes(id)
      ? t(`ops.monitor.mode.${id}`)
      : (id ?? dash);

  return (
    <div className="pf-ops-monitor">
      {error && (
        <ErrorBanner
          cause={t("ops.common.loadFailed")}
          action={t("ops.common.retry")}
          code={error}
        />
      )}
      {!data ? (
        !error && <Loading />
      ) : (
        <>
          <div className="pf-kpis" data-testid="monitor-tiles">
            {TILES.map((k) => (
              <div
                key={k}
                className={`pf-kpi${tileWarn(k, data.tiles) ? " pf-kpi--warn" : ""}`}
                data-tile={k}
              >
                <span className="pf-muted">{t(`ops.monitor.tile.${k}`)}</span>
                <b>{tileValue(k, data.tiles)}</b>
              </div>
            ))}
          </div>

          <section className="pf-pane" aria-labelledby="mon-rooms">
            <h2 id="mon-rooms" className="pf-h3">
              {t("ops.monitor.rooms")}
            </h2>
            {data.rooms.length === 0 ? (
              <p className="pf-muted">{t("ops.monitor.noRooms")}</p>
            ) : (
              <table className="pf-tbl" data-testid="monitor-rooms">
                <thead>
                  <tr>
                    <th scope="col">{t("ops.monitor.colRoom")}</th>
                    <th scope="col">{t("ops.monitor.colCourse")}</th>
                    <th scope="col">{t("ops.monitor.colMode")}</th>
                    <th scope="col">{t("ops.monitor.colMembers")}</th>
                    <th scope="col">{t("ops.monitor.colTick")}</th>
                    <th scope="col">{t("ops.monitor.colDegrade")}</th>
                    <th scope="col">
                      <span className="pf-sr-only">{t("ops.monitor.detail")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rooms.map((r) => (
                    <tr key={r.roomId} data-room={r.roomId}>
                      <th scope="row">{r.short}</th>
                      <td className="pf-muted">{r.course}</td>
                      <td>{modeLabel(r.modeId)}</td>
                      <td>
                        {r.members}/{r.capacity}
                      </td>
                      <td>{r.tickP95Ms == null ? dash : `${r.tickP95Ms.toFixed(1)}ms`}</td>
                      <td>
                        <span
                          className={`pf-chip-static${r.degrade > 0 ? " pf-chip-static--run" : ""}`}
                        >
                          {r.degrade > 0 ? `S${r.degrade}` : dash}
                        </span>
                      </td>
                      <td className="pf-tbl__act">
                        <Button
                          variant="ghost"
                          className="pf-btn--sm pf-ops-detail"
                          aria-label={t("ops.monitor.detailOf", { room: r.short })}
                          data-testid="monitor-detail"
                          onClick={() => void openDetail(r.roomId)}
                        >
                          {t("ops.monitor.detail")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {alerts.map((a) => (
              <div
                key={alertKey(a)}
                className="pf-banner pf-banner--warn"
                role="status"
                data-testid="monitor-alert"
                data-kind={a.kind}
              >
                <div className="pf-banner__text">
                  ⚠ {a.short ? `${a.short} ` : ""}
                  {a.detail}
                </div>
                <Button
                  variant="ghost"
                  className="pf-btn--sm"
                  aria-label={t("ops.monitor.ackOf", { room: a.short ?? a.kind })}
                  onClick={() => setAcked((s) => new Set(s).add(alertKey(a)))}
                >
                  {t("ops.monitor.ack")}
                </Button>
              </div>
            ))}
          </section>

          <section className="pf-pane" aria-labelledby="mon-pods" data-testid="monitor-pods">
            <h2 id="mon-pods" className="pf-h3">
              {t("ops.monitor.pods")}
            </h2>
            <table className="pf-tbl">
              <thead>
                <tr>
                  <th scope="col">{t("ops.monitor.colPod")}</th>
                  <th scope="col">{t("ops.monitor.colPodRooms")}</th>
                  <th scope="col">{t("ops.monitor.colPodState")}</th>
                  <th scope="col">
                    <span className="pf-sr-only">{t("ops.monitor.drainOn")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.pods.map((p) => (
                  <tr key={p.podId} data-pod={p.podId} data-draining={p.draining}>
                    <th scope="row">
                      <code>{p.podId}</code>
                    </th>
                    <td>
                      {p.rooms}/{p.capacity}
                    </td>
                    <td>
                      <span className="pf-row pf-row--wrap pf-chips">
                        <span
                          className={`pf-chip-static ${p.alive ? "pf-chip-static--ok" : "pf-chip-static--err"}`}
                        >
                          {t(p.alive ? "ops.monitor.podAlive" : "ops.monitor.podDead", {
                            s: p.lastSeenS ?? dash,
                          })}
                        </span>
                        {p.draining && (
                          <span className="pf-chip-static pf-chip-static--warn">
                            {t("ops.monitor.podDraining")}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="pf-tbl__act">
                      <Button
                        variant="ghost"
                        className="pf-btn--sm"
                        aria-pressed={p.draining}
                        aria-label={t("ops.monitor.drainOf", { pod: p.podId })}
                        disabled={busyPod !== null}
                        data-testid="pod-drain"
                        onClick={() => void drain(p)}
                      >
                        {t(p.draining ? "ops.monitor.drainOff" : "ops.monitor.drainOn")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="pf-note">{t("ops.monitor.drainNote")}</p>
          </section>

          <section className="pf-pane" aria-labelledby="mon-cat" data-testid="monitor-catalog">
            <h2 id="mon-cat" className="pf-h3">
              {t("ops.monitor.catalog")}
            </h2>
            <div className="pf-row pf-row--wrap">
              <div className="pf-state">
                {CATALOG_TYPES.map((ty, i) => (
                  <span
                    key={ty}
                    className={`pf-state__pill${i === 0 ? " pf-state__pill--on" : ""}`}
                  >
                    {t(`ops.catalog.type.${ty}`)} {catalog?.get(ty) ?? 0}
                  </span>
                ))}
              </div>
              <ButtonLink to="/ops/catalog" variant="green" className="pf-btn--sm pf-row__end">
                {t("ops.monitor.openCatalog")}
              </ButtonLink>
            </div>
          </section>
        </>
      )}
      {detail && <DetailDialog detail={detail} onClose={() => setDetail(null)} mode={modeLabel} />}
      <ToastHost items={toasts.items} onDismiss={toasts.dismiss} pico={false} />
    </div>
  );
}

function DetailDialog({
  detail,
  onClose,
  mode,
}: {
  detail: RoomDetail | "loading";
  onClose: () => void;
  mode: (id: string | null) => string;
}) {
  const t = useT();
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => close.current?.focus(), []);
  const d = detail === "loading" ? null : detail;
  const pad = (n: number) => String(n).padStart(2, "0");
  const time = (iso: string | null) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime())
      ? `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      : dash;
  };
  return (
    <div
      className="pf-modal"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div
        className="pf-pane pf-pane--hero pf-modal__pane pf-ops-room"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mon-detail-title"
        data-testid="monitor-room-detail"
      >
        <div className="pf-row">
          <h2 id="mon-detail-title" className="pf-h3">
            {t("ops.monitor.detailOf", { room: d?.short ?? d?.roomId ?? "" })}
          </h2>
          <Button ref={close} variant="ghost" className="pf-btn--sm pf-row__end" onClick={onClose}>
            {t("ops.monitor.close")}
          </Button>
        </div>
        {!d ? (
          <Loading />
        ) : (
          <>
            <div className="pf-row pf-row--wrap pf-chips">
              {d.course && <span className="pf-chip-static">{d.course}</span>}
              {d.modeId && (
                <span className="pf-chip-static pf-chip-static--info">{mode(d.modeId)}</span>
              )}
              {d.podId && (
                <span className="pf-chip-static">
                  {t("ops.monitor.detailPod", { pod: d.podId })}
                </span>
              )}
              {d.openedAt && (
                <span className="pf-chip-static">
                  {t("ops.monitor.detailOpened", { at: time(d.openedAt) })}
                </span>
              )}
            </div>
            <h3 className="pf-h3">{t("ops.monitor.timeline")}</h3>
            {d.events.length === 0 ? (
              <p className="pf-muted">{t("ops.monitor.noEvents")}</p>
            ) : (
              <div className="pf-tbl-scroll">
                <table className="pf-tbl" data-testid="monitor-timeline">
                  <tbody>
                    {d.events.map((e, i) => (
                      <tr key={i}>
                        <td className="pf-muted">{time(e.at)}</td>
                        <td>
                          <code>{e.kind}</code>
                        </td>
                        <td className="pf-muted">{e.pid ?? dash}</td>
                        <td>{e.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
